import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ibanToBank, BANK_LIST } from "@/lib/banksDe";
import {
  ShieldCheck, Loader2, AlertCircle, ChevronLeft, CheckCircle2, RotateCcw,
  FileSignature, Sparkles, Download, Search, ChevronsUpDown, Info,
} from "lucide-react";
import { toast } from "sonner";

/** Mod-97 IBAN check (matches backend logic) — used purely for inline UX feedback. */
function isValidIBAN(iban) {
  const n = (iban || "").toUpperCase().replace(/\s+/g, "");
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(n)) return false;
  const rearranged = n.slice(4) + n.slice(0, 4);
  const digits = [...rearranged].map((c) => /[A-Z]/.test(c) ? (c.charCodeAt(0) - 55).toString() : c).join("");
  let rem = 0;
  for (const ch of digits) rem = (rem * 10 + Number(ch)) % 97;
  return rem === 1;
}

const formatIBAN = (s) => (s || "").toUpperCase().replace(/\s+/g, "").replace(/(.{4})/g, "$1 ").trim();

/** Wrapper außerhalb des Default-Exports — verhindert React-Re-Mount der
 *  Eingabefelder bei jedem Tastendruck (sonst würde Inline-Wrap als
 *  „neue Komponente" erkannt → focus loss / Felder klappen nicht). */
function Wrap({ embedded, title, subtitle, children }) {
  if (embedded) return <div className="px-1 sm:px-2 pt-1 pb-2">{children}</div>;
  return <Layout title={title} subtitle={subtitle}>{children}</Layout>;
}

/**
 * SEPA-Mandat-Seite — kann sowohl als Standalone-Route (`/billing/sepa`) als
 * auch als eingebettete Komponente in `Billing.jsx` benutzt werden.
 *
 * Props (optional, beim Embed gesetzt):
 *   - plan: string                — vorgewählter Plan-ID
 *   - embedded: boolean           — versteckt Layout/Header, Inline-Container
 *   - onCompleted: (mandate, plan) => void
 */
export default function SepaMandate({ plan: planProp, embedded: embeddedProp, onCompleted } = {}) {
  const { user, refreshMe } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const preselectedPlan = planProp ?? searchParams.get("plan");
  const isEmbed = embeddedProp ?? (searchParams.get("embed") === "1");

  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [packages, setPackages] = useState({});
  const [activating, setActivating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  // Wenn aktives Mandat existiert: Formular ausblenden, nur Erfolgs-Kachel zeigen.
  // Klick auf „Mandat ersetzen" öffnet das Formular wieder zum Bearbeiten.
  const [formCollapsed, setFormCollapsed] = useState(false);

  const [form, setForm] = useState({
    debtor_name: user?.name || "",
    debtor_address: "",
    debtor_postal_code: "",
    debtor_city: "",
    iban: "",
    bic: "",
    bank_name: "",
    signer_name: user?.name || "",
  });
  const [confirmed, setConfirmed] = useState(false);

  // ---- Signature pad (react-signature-canvas) ----
  const sigRef = useRef(null);
  const [sigEmpty, setSigEmpty] = useState(true);
  const clearSignature = () => {
    sigRef.current?.clear();
    setSigEmpty(true);
  };

  // ---- IBAN → BIC + Bank-Name auto-fill ----
  const onIbanChange = (raw) => {
    const upper = (raw || "").toUpperCase();
    setForm((f) => {
      const next = { ...f, iban: upper };
      const bank = ibanToBank(upper);
      // Nur überschreiben, wenn der User die Felder noch nicht selbst gefüllt hat
      // (oder den vorigen Auto-Fill drinhat). Robust: leer ODER == letzter Auto-Fill.
      if (bank) {
        if (!f.bic || f.bic === f._lastAutoBic) {
          next.bic = bank.bic;
          next._lastAutoBic = bank.bic;
        }
        if (!f.bank_name || f.bank_name === f._lastAutoBank) {
          next.bank_name = bank.name;
          next._lastAutoBank = bank.name;
        }
      }
      return next;
    });
  };

  useEffect(() => {
    api.get("/sepa/mandate/me").then(({ data }) => {
      setExisting(data.mandate);
      // Beim Initial-Mount IMMER das Formular zeigen — auch wenn ein altes
      // Mandat aktiv ist. Die kompakte Erfolgs-Kachel erscheint erst NACH
      // einem frischen Submit (siehe submit-Funktion).
      // → formCollapsed bleibt bei false beim Mount.
    }).catch(() => {}).finally(() => setLoading(false));
    api.get("/billing/packages").then(({ data }) => setPackages(data.packages || {})).catch(() => {});

    // Kontoinhaber-Felder aus User-Profil auto-fill (nur leere Felder)
    api.get("/users/me/profile").then(({ data }) => {
      const u = data.user || {};
      setForm((f) => ({
        ...f,
        debtor_name: f.debtor_name || u.name || "",
        debtor_address: f.debtor_address || u.address_line || "",
        debtor_postal_code: f.debtor_postal_code || u.postal_code || "",
        debtor_city: f.debtor_city || u.city || "",
        signer_name: f.signer_name || u.name || "",
      }));
    }).catch(() => {});
  }, []);

  // Wenn ein aktives Mandat geladen wird, übernimm die existierenden Werte ins
  // Formular — so kann der User sie bei „Mandat bearbeiten" gleich überschreiben.
  useEffect(() => {
    if (!existing) return;
    setForm((f) => ({
      ...f,
      debtor_name: existing.debtor_name || f.debtor_name,
      debtor_address: existing.debtor_address || f.debtor_address,
      debtor_postal_code: existing.debtor_postal_code || f.debtor_postal_code,
      debtor_city: existing.debtor_city || f.debtor_city,
      iban: existing.iban || f.iban,
      bic: existing.bic || f.bic,
      bank_name: existing.bank_name || f.bank_name,
      signer_name: existing.signer_name || f.signer_name,
    }));
  }, [existing]);

  // Wenn das Mandat bereits existiert UND ?plan= angegeben ist, aktiviere den Plan direkt.
  useEffect(() => {
    if (!existing || existing.status !== "active") return;
    if (!preselectedPlan) return;
    if (user?.subscription_plan === preselectedPlan && user?.subscription_status === "active") return;
    setActivating(true);
    api.post("/billing/activate-plan", { plan_id: preselectedPlan })
      .then(async () => {
        await refreshMe();
        toast.success(`Plan „${packages[preselectedPlan]?.name || preselectedPlan}" aktiviert`);
        if (onCompleted) onCompleted(existing, preselectedPlan);
        else if (!isEmbed) navigate("/billing", { replace: true });
      })
      .catch((e) => toast.error(apiError(e, "Plan-Aktivierung fehlgeschlagen")))
      .finally(() => setActivating(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing, preselectedPlan]);

  const ibanValid = useMemo(() => isValidIBAN(form.iban), [form.iban]);
  const formReady = useMemo(() => {
    return form.debtor_name.trim() && form.debtor_address.trim() && form.debtor_postal_code.trim() &&
           form.debtor_city.trim() && form.signer_name.trim() && ibanValid && !sigEmpty && confirmed;
  }, [form, ibanValid, sigEmpty, confirmed]);

  const submit = async () => {
    if (!formReady) return;
    if (sigRef.current?.isEmpty()) {
      toast.error("Bitte unterschreiben Sie das Mandat im Unterschriftsfeld.");
      return;
    }
    setSubmitting(true);
    try {
      // Use toDataURL directly (NOT getTrimmedCanvas — wirft auf transparenten
      // Canvases einen Error). Hintergrund ist im PDF ohnehin weiß.
      const signatureDataUrl = sigRef.current.toDataURL("image/png");
      const { data } = await api.post("/sepa/mandate", {
        ...form,
        iban: form.iban.replace(/\s+/g, "").toUpperCase(),
        bic: form.bic.toUpperCase() || null,
        signature_data_url: signatureDataUrl,
      });
      setExisting(data.mandate);
      setFormCollapsed(true);    // Formular ausblenden → Kachel sichtbar
      toast.success(`SEPA-Mandat erteilt — Referenz ${data.mandate.mandate_reference}`);

      if (onCompleted) onCompleted(data.mandate, preselectedPlan || null);

      if (preselectedPlan) {
        try {
          setActivating(true);
          await api.post("/billing/activate-plan", { plan_id: preselectedPlan });
          await refreshMe();
          toast.success(`Plan „${packages[preselectedPlan]?.name || preselectedPlan}" aktiviert`);
          if (!onCompleted && !isEmbed) navigate("/billing", { replace: true });
        } catch (e) {
          toast.error(apiError(e, "Plan konnte nicht automatisch aktiviert werden — bitte manuell wählen"));
        } finally {
          setActivating(false);
        }
      }
    } catch (e) {
      toast.error(apiError(e, "Mandat konnte nicht gespeichert werden"));
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      // Wir nutzen `axios.get` mit `responseType: 'blob'` — falls der Server
      // einen Fehler (z. B. 401) wirft, wäre `res.data` ein Blob mit JSON-Error,
      // den wir extra prüfen.
      const res = await api.get("/sepa/mandate/me/pdf", {
        responseType: "blob",
        // Manchmal interceptiert axios den Content-Type beim Refresh-Retry.
        // Mit explizitem Accept stellen wir sicher, dass das Backend PDF liefert.
        headers: { Accept: "application/pdf" },
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      if (blob.size < 500) {
        // Vermutlich ein Fehler-JSON statt PDF zurückgekommen.
        const text = await blob.text();
        // eslint-disable-next-line no-console
        console.warn("PDF too small, server response:", text);
        throw new Error("PDF konnte nicht erzeugt werden");
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SEPA-Mandat_${existing?.mandate_reference || "Mandat"}.pdf`;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      // Cleanup nach kurzer Verzögerung — manche Browser brauchen den Link
      // noch im DOM, bis der Download wirklich startet.
      setTimeout(() => {
        a.remove();
        URL.revokeObjectURL(url);
      }, 500);
      toast.success("PDF wird heruntergeladen");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("PDF download error:", e);
      toast.error(apiError(e, "PDF konnte nicht erzeugt werden"));
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <Wrap embedded={isEmbed} title="SEPA-Lastschriftmandat">
        <div className="flex items-center justify-center py-20 text-zinc-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Lade…
        </div>
      </Wrap>
    );
  }

  if (activating) {
    return (
      <Wrap embedded={isEmbed} title="Plan wird aktiviert …">
        <div className="max-w-xl mx-auto py-12 sm:py-16 text-center" data-testid="sepa-activating">
          <div className="mx-auto mb-4 h-14 w-14 rounded-2xl brand-gradient-bg flex items-center justify-center">
            <Loader2 className="h-7 w-7 animate-spin text-white" />
          </div>
          <h1 className="font-display text-2xl font-bold tracking-tight">Einen Moment …</h1>
          <p className="text-sm text-zinc-600 mt-2">Ihr Plan wird auf Basis des SEPA-Mandats aktiviert.</p>
        </div>
      </Wrap>
    );
  }

  // Kompakte „Mandat erteilt"-Kachel — wird angezeigt, wenn das Mandat aktiv ist
  // UND der User das Formular nicht zur Bearbeitung geöffnet hat.
  if (existing && existing.status === "active" && formCollapsed) {
    const ibanMasked = existing.iban?.length >= 8
      ? `${existing.iban.slice(0, 4)} ••• ${existing.iban.slice(-4)}` : existing.iban;
    return (
      <Wrap embedded={isEmbed} title="SEPA-Lastschriftmandat" subtitle="Mandat erteilt">
        <div className="max-w-3xl mx-auto">
          {!isEmbed && (
            <Link to="/billing" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-4">
              <ChevronLeft className="h-4 w-4" /> Zurück zur Abrechnung
            </Link>
          )}
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-5 sm:p-6 flex items-start gap-4" data-testid="sepa-mandate-active">
            <div className="h-12 w-12 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0">
              <ShieldCheck className="h-6 w-6" strokeWidth={1.8} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-700">Mandat erteilt · Aktiv</div>
              <div className="font-display text-xl sm:text-2xl font-bold tracking-tight mt-0.5">
                {existing.mandate_reference}
              </div>
              <div className="text-sm text-zinc-600 mt-1">
                {existing.debtor_name} · IBAN <span className="font-mono">{ibanMasked}</span>
                {existing.bank_name ? ` · ${existing.bank_name}` : ""}
              </div>
              <div className="text-xs text-zinc-500 mt-1">
                Erteilt am {new Date(existing.signed_at).toLocaleString("de-DE")}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button onClick={downloadPdf} disabled={downloading} className="brand-gradient-bg text-white" data-testid="sepa-download-pdf">
                  {downloading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
                  PDF herunterladen
                </Button>
                <Button variant="outline" onClick={() => setFormCollapsed(false)} data-testid="sepa-edit-btn">
                  Mandat bearbeiten
                </Button>
                {!isEmbed && (
                  <Link to="/billing"><Button variant="ghost">Zurück zur Abrechnung</Button></Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </Wrap>
    );
  }

  // Form view
  return (
    <Wrap embedded={isEmbed} title="SEPA-Lastschriftmandat" subtitle="Bitte tragen Sie Ihre Bankverbindung ein und unterschreiben Sie unten.">
      <div className="max-w-3xl mx-auto">
        {!isEmbed && (
          <Link to="/billing" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-4">
            <ChevronLeft className="h-4 w-4" /> Zurück zur Abrechnung
          </Link>
        )}

        {user?.sepa_mandate_missing && (
          <div className="rounded-2xl border-2 border-rose-300 bg-rose-50 p-4 sm:p-5 mb-5 flex items-start gap-3" data-testid="sepa-required-banner">
            <div className="h-10 w-10 rounded-xl bg-rose-600 text-white flex items-center justify-center shrink-0">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-bold text-rose-900">Zugriff gesperrt — SEPA-Mandat erforderlich</div>
              <div className="text-xs text-rose-800/90 mt-1">
                Dein SEPA-Lastschriftmandat wurde widerrufen oder fehlt. Die Software ist gesperrt,
                bis du unten ein neues Mandat erteilst. Danach ist sofort wieder alles freigeschaltet.
              </div>
            </div>
          </div>
        )}

        <div className="rounded-3xl border border-cyan-100 bg-gradient-to-br from-cyan-50/40 via-white to-emerald-50/30 p-6 sm:p-9" data-testid="sepa-mandate-form">
          {preselectedPlan && packages[preselectedPlan] && (
            <div className="rounded-2xl border-2 border-cyan-300 bg-gradient-to-r from-cyan-600 to-emerald-600 text-white p-4 sm:p-5 mb-6" data-testid="sepa-plan-banner">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center shrink-0">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.24em] font-bold opacity-90">
                    Sie aktivieren den Tarif
                  </div>
                  <div className="font-display text-xl sm:text-2xl font-bold tracking-tight mt-0.5">
                    {packages[preselectedPlan].name} · {packages[preselectedPlan].amount.toFixed(0)} €/{packages[preselectedPlan].interval}
                  </div>
                  <div className="text-xs sm:text-sm opacity-90 mt-1">
                    Nach Erteilung des SEPA-Mandats wird der Plan automatisch aktiv — keine Kreditkarte nötig.
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-start gap-3 mb-6">
            <div className="h-12 w-12 rounded-2xl brand-gradient-bg flex items-center justify-center shrink-0">
              <FileSignature className="h-6 w-6 text-white" strokeWidth={1.7} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-700">SEPA-Lastschriftmandat</div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">
                Online-Mandat erteilen
              </h1>
              <p className="text-sm text-zinc-600 mt-1.5">
                Damit ermächtigen Sie EasyGutachter, monatliche Beiträge zur Subscription
                per SEPA-Lastschrift einzuziehen.
              </p>
            </div>
          </div>

          {/* Creditor info (read-only) */}
          <div className="rounded-2xl border-2 border-cyan-200 bg-gradient-to-br from-cyan-50/60 via-white to-emerald-50/30 p-5 mb-5" data-testid="creditor-id-card">
            <div className="flex items-start gap-3">
              <div className="h-9 w-9 rounded-xl bg-cyan-600 text-white flex items-center justify-center shrink-0">
                <ShieldCheck className="h-4.5 w-4.5" strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-cyan-700">
                  Zahlungsempfänger / Gläubiger
                </div>
                <div className="text-base font-semibold text-zinc-900 mt-0.5">EasyGutachter</div>
                <div className="text-xs text-zinc-600">Im Mediapark 8, 50670 Köln</div>
                <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Gläubiger-ID:</span>
                  <span className="font-mono text-sm font-bold text-cyan-900 px-2 py-0.5 rounded bg-cyan-100" data-testid="creditor-id-form-value">
                    DE87CCP00002910828
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Debtor info */}
          <div className="rounded-2xl border border-zinc-200 bg-white p-5 space-y-4 mb-5">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-cyan-700">Kontoinhaber</div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Name *">
                <Input value={form.debtor_name} onChange={(e) => setForm({ ...form, debtor_name: e.target.value })} data-testid="sepa-debtor-name" />
              </Field>
              <Field label="Adresse *">
                <Input value={form.debtor_address} onChange={(e) => setForm({ ...form, debtor_address: e.target.value })} placeholder="Hauptstr. 12" data-testid="sepa-debtor-address" />
              </Field>
              <Field label="PLZ *">
                <Input value={form.debtor_postal_code} onChange={(e) => setForm({ ...form, debtor_postal_code: e.target.value.replace(/\D/g, "").slice(0, 5) })} placeholder="10115" data-testid="sepa-debtor-plz" />
              </Field>
              <Field label="Ort *">
                <Input value={form.debtor_city} onChange={(e) => setForm({ ...form, debtor_city: e.target.value })} placeholder="Berlin" data-testid="sepa-debtor-city" />
              </Field>
            </div>
          </div>

          {/* Bank info */}
          <div className="rounded-2xl border border-zinc-200 bg-white p-5 space-y-4 mb-5">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-cyan-700">Bankverbindung</div>

            <div className="rounded-lg bg-cyan-50/60 border border-cyan-200 px-3 py-2 text-xs text-cyan-900 flex items-start gap-2" data-testid="sepa-iban-hint">
              <Info className="h-3.5 w-3.5 text-cyan-600 shrink-0 mt-0.5" />
              <span>
                Tippen Sie Ihre IBAN — <strong>BIC und Bankname werden automatisch ergänzt</strong>.
                Falls Ihre Bank nicht erkannt wird, können Sie sie unten manuell auswählen oder selbst eintragen.
              </span>
            </div>

            <Field label="IBAN *" hint={form.iban && !ibanValid ? "Ungültige IBAN — bitte prüfen" : null}>
              <Input
                value={formatIBAN(form.iban)}
                onChange={(e) => onIbanChange(e.target.value)}
                placeholder="DE00 0000 0000 0000 0000 00"
                className={`font-mono text-base h-11 ${form.iban && !ibanValid ? "border-rose-400 focus-visible:ring-rose-400/40" : ibanValid ? "border-emerald-400 focus-visible:ring-emerald-400/40" : ""}`}
                data-testid="sepa-iban"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck="false"
              />
            </Field>
            {ibanValid && form.bank_name && (
              <div className="rounded-lg bg-emerald-50/60 border border-emerald-200 px-3 py-2 text-xs text-emerald-900 flex items-center gap-2" data-testid="sepa-bank-detected">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                <span><strong>{form.bank_name}</strong> automatisch erkannt — BIC <span className="font-mono">{form.bic}</span></span>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="BIC">
                <Input
                  value={form.bic}
                  onChange={(e) => setForm({ ...form, bic: e.target.value.toUpperCase(), _lastAutoBic: undefined })}
                  placeholder="COBADEFFXXX"
                  className="font-mono"
                  data-testid="sepa-bic"
                />
              </Field>
              <Field label="Bank">
                <BankAutocomplete
                  value={form.bank_name}
                  onChange={(v) => setForm({ ...form, bank_name: v, _lastAutoBank: undefined })}
                  onSelect={(bank) => setForm({ ...form, bank_name: bank.name, bic: bank.bic, _lastAutoBic: bank.bic, _lastAutoBank: bank.name })}
                  testid="sepa-bank"
                />
              </Field>
            </div>
            <div className="text-[11px] text-zinc-500 -mt-1">
              Tipp: Geben Sie nur den Anfangsbuchstaben ein (z. B. <strong>„C"</strong> → Commerzbank, Consorsbank …) — die Liste filtert automatisch.
            </div>
          </div>

          {/* Mandate text + confirmation */}
          <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5 mb-5">
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-amber-700 mb-2">Mandatstext</div>
            <p className="text-sm text-zinc-800 leading-relaxed">
              Ich ermächtige <strong>EasyGutachter</strong>, Gläubiger-ID
              <span className="font-mono mx-1 px-1.5 py-0.5 rounded bg-white border border-amber-300 text-amber-900">DE87CCP00002910828</span>,
              Zahlungen von meinem Konto mittels Lastschrift einzuziehen.
              Zugleich weise ich mein Kreditinstitut an, die von EasyGutachter auf mein Konto gezogenen
              Lastschriften einzulösen. Ich kann innerhalb von <strong>zwei Wochen</strong>, beginnend mit dem Belastungsdatum,
              die Erstattung des belasteten Betrages verlangen. Es gelten dabei die mit meinem Kreditinstitut
              vereinbarten Bedingungen.
            </p>
            <p className="text-xs text-zinc-500 mt-3">
              Die Mandatsreferenz wird Ihnen nach erfolgreicher Erteilung mitgeteilt.
            </p>
            <label className="mt-4 flex items-start gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                className="mt-1 h-4 w-4 accent-cyan-600"
                data-testid="sepa-confirm-checkbox"
              />
              <span className="text-zinc-800">
                Ich bestätige obige Ermächtigung und habe den Mandatstext gelesen.
              </span>
            </label>
          </div>

          {/* Signature */}
          <div className="rounded-2xl border border-zinc-200 bg-white p-5 mb-5">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-cyan-700">Unterschrift Kontoinhaber</div>
                <div className="text-xs text-zinc-500 mt-0.5">Bitte mit Maus oder Finger unterschreiben.</div>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={clearSignature} data-testid="sepa-signature-clear">
                <RotateCcw className="h-4 w-4 mr-1" /> Löschen
              </Button>
            </div>
            <div className="rounded-xl border-2 border-dashed border-zinc-300 bg-zinc-50 overflow-hidden">
              <SignatureCanvas
                ref={sigRef}
                penColor="#0b1320"
                onEnd={() => setSigEmpty(sigRef.current?.isEmpty() ?? true)}
                canvasProps={{
                  className: "w-full h-44 sm:h-48 touch-none",
                  style: { touchAction: "none", display: "block", width: "100%", height: "11rem" },
                  "data-testid": "sepa-signature-canvas",
                }}
              />
            </div>
            {sigEmpty && (
              <div className="text-[11px] text-zinc-500 mt-2">Kein Strich erkannt — bitte im Feld unterschreiben.</div>
            )}
            <div className="mt-3">
              <Field label="Name des Unterzeichners *">
                <Input value={form.signer_name} onChange={(e) => setForm({ ...form, signer_name: e.target.value })} data-testid="sepa-signer-name" />
              </Field>
            </div>
          </div>

          {!ibanValid && form.iban && (
            <div className="rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-xs p-3 mb-4 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              IBAN-Prüfziffer ist nicht korrekt. Bitte prüfen.
            </div>
          )}

          <Button
            onClick={submit}
            disabled={!formReady || submitting}
            className="w-full brand-gradient-bg text-white h-12 text-base font-semibold"
            data-testid="sepa-submit"
          >
            {submitting ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <CheckCircle2 className="h-5 w-5 mr-2" />}
            Mandat verbindlich erteilen
          </Button>
        </div>
      </div>
    </Wrap>
  );
}

function Field({ label, children, hint }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.18em] font-semibold text-zinc-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="text-[11px] text-rose-600 mt-1">{hint}</div>}
    </div>
  );
}

function InfoRow({ label, children, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">{label}</div>
      <div className={`text-sm font-semibold text-zinc-900 mt-0.5 ${mono ? "font-mono" : ""}`}>{children}</div>
    </div>
  );
}

/**
 * Type-Ahead-Dropdown für deutsche Banken. Listet beim Tippen automatisch
 * passende Treffer aus `BANK_LIST` (Bundesbank-Auswahl). Nutzer kann auch
 * eine eigene Bank eintragen — der Wert ist nicht auf Listen-Einträge
 * beschränkt (free-form).
 */
function BankAutocomplete({ value, onChange, onSelect, testid }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Schließt bei Klick außerhalb
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const matches = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    if (!q) return BANK_LIST.slice(0, 20);                      // beim Öffnen: erste 20
    if (q.length <= 2) {
      // Bei 1–2 Zeichen: nur Banken, die mit dieser Folge beginnen
      // (z. B. „C" → Comdirect, Commerzbank, Consorsbank — nicht „Bayerische")
      return BANK_LIST.filter((b) => b.name.toLowerCase().startsWith(q)).slice(0, 30);
    }
    return BANK_LIST.filter(
      (b) => b.name.toLowerCase().includes(q) || b.bic.toLowerCase().includes(q)
    ).slice(0, 30);
  }, [value]);

  return (
    <div className="relative" ref={wrapRef}>
      <div className="relative">
        <Input
          value={value || ""}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="z. B. Commerzbank"
          className="pr-9"
          data-testid={testid}
        />
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
          aria-label="Bank-Liste öffnen"
          data-testid={`${testid}-toggle`}
        >
          <ChevronsUpDown className="h-4 w-4" />
        </button>
      </div>
      {open && matches.length > 0 && (
        <div
          className="absolute z-30 mt-1 left-0 right-0 max-h-72 overflow-y-auto rounded-lg border border-zinc-200 bg-white shadow-xl divide-y divide-zinc-100"
          data-testid={`${testid}-dropdown`}
        >
          {matches.map((b) => (
            <button
              key={`${b.bic}-${b.blz}`}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(b);
                setOpen(false);
              }}
              className="block w-full text-left px-3 py-2 hover:bg-cyan-50 focus:bg-cyan-50 focus:outline-none"
              data-testid={`${testid}-item-${b.bic}`}
            >
              <div className="text-sm font-medium text-zinc-900">{b.name}</div>
              <div className="text-[11px] font-mono text-zinc-500">{b.bic}</div>
            </button>
          ))}
        </div>
      )}
      {open && matches.length === 0 && (
        <div className="absolute z-30 mt-1 left-0 right-0 rounded-lg border border-zinc-200 bg-white shadow-lg p-3 text-xs text-zinc-500" data-testid={`${testid}-noresults`}>
          <Search className="h-3.5 w-3.5 inline -mt-0.5 mr-1" />
          Keine Bank gefunden — Sie können den Namen einfach selbst eintragen.
        </div>
      )}
    </div>
  );
}
